/**
 * Runtime decoder for React minified errors.
 * Patches console.error so that "Minified React error #XXX" is replaced with
 * the full message in staging/production, making debugging easier.
 *
 * See https://react.dev/errors for the full list.
 */
const REACT_ERROR_MESSAGES: Record<number, string> = {
  329: 'Unknown root exit status.',
  418: `Hydration failed because the server rendered content didn't match the client. As a result this tree will be regenerated on the client. This can happen if a SSR-ed Client Component used: A server/client branch (e.g. if (typeof window !== 'undefined')), variable input such as Date.now() or Math.random(), date formatting in a user's locale which doesn't match the server, external changing data without sending a snapshot, invalid HTML tag nesting, or a browser extension messing with the HTML before React loaded. See https://react.dev/link/hydration-mismatch`,
  423: 'There was an error while hydrating but React was able to recover by instead client rendering the entire root.',
  // Add more codes as needed; full list at https://react.dev/errors
};

const MINIFIED_REACT_ERROR_RE = /Minified React error #(\d+)/g;

function decodeReactError(message: string): string {
  const decoded = message.replace(MINIFIED_REACT_ERROR_RE, (_match, codeStr) => {
    const code = parseInt(codeStr, 10);
    const full =
      REACT_ERROR_MESSAGES[code] ?? `(See https://react.dev/errors/${code} for the full message)`;
    return `[React #${code}] ${full}`;
  });
  return decoded;
}

function hasMinifiedReactError(message: unknown): boolean {
  if (typeof message !== 'string') return false;
  return message.includes('Minified React error #');
}

/**
 * Patches console.error so that any "Minified React error #XXX" in the first
 * argument is decoded to the full message before logging. Call once from a
 * client component (e.g. root layout or Providers) so staging/production
 * gets readable React errors.
 */
export function patchConsoleForReactErrors(): void {
  if (typeof window === 'undefined') return;

  const originalError = console.error;
  console.error = function (...args: unknown[]) {
    const first = args[0];
    if (first !== undefined && hasMinifiedReactError(first)) {
      const decoded = decodeReactError(String(first));
      originalError.call(console, decoded, ...args.slice(1));
      return;
    }
    originalError.apply(console, args);
  };
}
